import { AxiosResponse } from "axios";
import { endPoints, leadTypes, sharedLeads } from "utils/constants";
import { crudService } from "services/crud.service";
import { NotificationManager } from "react-notifications";
import {
  defaultMetaObj,
  deleteErrorResponse,
  initialLeadState,
  isPolishLead,
  leadDeleteErrorModified,
} from "store/constants";
import {
  getErrorMessage,
  getPaginatedUrlWithSearchParams,
  polishLeadParam,
} from "store/helper";
import { PayloadAction, createSlice, createAsyncThunk } from "@reduxjs/toolkit";

const {
  rawLeadEndPoint,
  polishLeadEndPoint,
  leadEndPoint,
  sharedLeadEndpoint,
} = endPoints;

const { rawLead, polishedLead } = leadTypes;

export const fetchAllCommonLeads = createAsyncThunk(
  "leads/fetchAllCommonLeads",
  async (
    { page, searchParamsObj = {} }: { page: number; searchParamsObj?: {} },
    { rejectWithValue }
  ) => {
    const paginatedUrl = getPaginatedUrlWithSearchParams({
      pageNumber: page,
      endpoint: leadEndPoint,
      searchParams: {
        ...searchParamsObj,
      },
    });

    try {
      const response = await crudService.getAll(paginatedUrl);

      return response.data;
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAllLeads = createAsyncThunk(
  "leads/fetchAllLeads",
  async (
    {
      page,
      limit,
      searchParamsObj = {},
    }: { page: number; limit?: number; searchParamsObj?: {} },
    { rejectWithValue }
  ) => {
    const sharedLeadCondition: boolean =
      Object.keys(searchParamsObj)?.includes(isPolishLead);
    const polishLeadkey = polishLeadParam({ searchParamsObj, type: rawLead });

    const paginatedUrl = getPaginatedUrlWithSearchParams({
      pageNumber: page,
      limit,
      endpoint: sharedLeadCondition ? sharedLeadEndpoint : leadEndPoint,
      searchParams: {
        ...searchParamsObj,
        ...polishLeadkey,
      },
    });

    try {
      const response = await crudService.getAll(paginatedUrl);

      return response.data;
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAllPolishedLeads = createAsyncThunk(
  "leads/fetchAllPolishedLeads",
  async (
    {
      page,
      limit,
      searchParamsObj = {},
    }: { page: number; limit?: number; searchParamsObj?: {} },
    { rejectWithValue }
  ) => {
    const sharedLeadCondition: boolean =
      Object.keys(searchParamsObj)?.includes(isPolishLead);
    const polishLeadkey = polishLeadParam({
      searchParamsObj,
      type: polishedLead,
    });

    const paginatedUrl = getPaginatedUrlWithSearchParams({
      pageNumber: page,
      limit,
      endpoint: sharedLeadCondition ? sharedLeadEndpoint : leadEndPoint,
      searchParams: {
        ...searchParamsObj,
        ...polishLeadkey,
      },
    });
    try {
      const response = await crudService.getAll(paginatedUrl);

      return response.data;
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const addRawLeads = createAsyncThunk(
  "leads/addRawLeads",
  async (newLead: any, { rejectWithValue }) => {
    try {
      const response: AxiosResponse = await crudService.create(
        `${leadEndPoint}/`,
        newLead
      );

      if (response.status === 201) {
        const messageToDisplay = response?.data?.[isPolishLead]
          ? "Successfully created new lead in Polished Leads View."
          : "Successfully created new lead in Raw Leads View.";
        NotificationManager.success(messageToDisplay, "", 10000);
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const addPolishedLeads = createAsyncThunk(
  "leads/addPolishedLeads",
  async (newLead: any, { rejectWithValue }) => {
    try {
      const response: AxiosResponse = await crudService.create(
        `${leadEndPoint}/`,
        newLead
      );

      if (response.status === 201) {
        const messageToDisplay = response?.data?.[isPolishLead]
          ? "Successfully created new lead in Polished Leads View."
          : "Successfully created new lead in Raw Leads View.";
        NotificationManager.success(messageToDisplay, "", 10000);
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const updateLead = createAsyncThunk(
  "leads/updateLead",
  async ({ updatedLead, leadId, variant }: any, { rejectWithValue }) => {
    try {
      const response: AxiosResponse = await crudService.update(
        `${leadEndPoint}`,
        leadId,
        updatedLead
      );
      if (response.status === 200) {
        const messageToDisplay =
          response?.data?.[isPolishLead] === updatedLead?.[isPolishLead]
            ? "Successfully updated."
            : "Successfully updated and the lead moved to Polished Leads View.";
        if (variant === sharedLeads) {
          NotificationManager.success(
            "Request to share lead has been sent for approval.",
            "",
            10000
          );
          return response.data;
        }

        if (variant === "link-customer") {
          NotificationManager.success(
            "Lead successfully linked to current customer.",
            "",
            10000
          );
          return response.data;
        }

        NotificationManager.success(messageToDisplay, "", 10000);
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

// export const updatePolishLead = createAsyncThunk(
//   "leads/updatePolishLead",
//   async ({ updatedLead, leadId }: any, { rejectWithValue }) => {
//     try {
//       const response: AxiosResponse = await crudService.update(
//         `${polishLeadEndPoint}`,
//         leadId,
//         updatedLead
//       );
//       return response.data;
//     } catch (error: any) {
//       const errorMessage = getErrorMessage(error);

//       NotificationManager.error(errorMessage);
//       return rejectWithValue(error.message);
//     }
//   }
// );

export const deleteRawLead = createAsyncThunk(
  "leads/deleteRawLead",
  async ({ leadId }: any, { rejectWithValue }) => {
    try {
      const response: any = await crudService.delete(
        `${rawLeadEndPoint}`,
        leadId
      );

      if (response.status === 204) {
        NotificationManager.success("Lead Deleted Successfully.");
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(
        errorMessage?.includes(deleteErrorResponse)
          ? leadDeleteErrorModified
          : errorMessage
      );

      return rejectWithValue(error.message);
    }
  }
);

export const deletePolishedLead = createAsyncThunk(
  "leads/deletePolishedLead",
  async ({ leadId }: any, { rejectWithValue }) => {
    try {
      const response: any = await crudService.delete(
        `${polishLeadEndPoint}`,
        leadId
      );

      if (response.status === 204) {
        NotificationManager.success("Lead Deleted Successfully.");
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(
        errorMessage?.includes(deleteErrorResponse)
          ? leadDeleteErrorModified
          : errorMessage
      );
      return rejectWithValue(error.message);
    }
  }
);

const leadSlice = createSlice({
  name: "leads",
  initialState: initialLeadState,
  reducers: {},
  extraReducers: (builder) => {
    // fetch all common leads
    builder
      .addCase(fetchAllCommonLeads.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        fetchAllCommonLeads.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.leads["common"] = action.payload?.objects as any[];
          state.meta = action.payload?.meta;
        }
      )
      .addCase(
        fetchAllCommonLeads.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
          state.leads["common"] = [];
          state.meta = defaultMetaObj;
        }
      );

    // fetch all raw leads
    builder
      .addCase(fetchAllLeads.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        fetchAllLeads.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.leads["raw-lead"] = action.payload?.objects as any[];
          state.meta = action.payload?.meta;
        }
      )
      .addCase(
        fetchAllLeads.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
          state.leads["raw-lead"] = [];
          state.meta = defaultMetaObj;
        }
      );

    // all polished leads
    builder
      .addCase(fetchAllPolishedLeads.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        fetchAllPolishedLeads.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.leads["polished-lead"] = action.payload?.objects as any[];
          state.meta = action.payload?.meta;
        }
      )
      .addCase(
        fetchAllPolishedLeads.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
          state.leads["polished-lead"] = [];
          state.meta = defaultMetaObj;
        }
      );

    //   update lead
    builder
      .addCase(updateLead.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        updateLead.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
        }
      )
      .addCase(
        updateLead.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
        }
      );

    // //   update lead
    // builder
    //   .addCase(updatePolishLead.pending, (state: any) => {
    //     state.loading = true;
    //   })
    //   .addCase(
    //     updatePolishLead.fulfilled,
    //     (state: any, action: PayloadAction<any>) => {
    //       state.loading = false;
    //     }
    //   )
    //   .addCase(
    //     updatePolishLead.rejected,
    //     (state: any, action: PayloadAction<any>) => {
    //       state.loading = false;
    //       state.error = action.payload as string;
    //     }
    //   );

    // add raw Lead
    builder
      .addCase(addRawLeads.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(addRawLeads.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        // state.leads = [...state.leads, action.payload as any];
      })
      .addCase(addRawLeads.rejected, (state: any, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // add polished Lead
    builder
      .addCase(addPolishedLeads.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        addPolishedLeads.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
        }
      )
      .addCase(addPolishedLeads.rejected, (state: any, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // delete raw Lead
    builder
      .addCase(deleteRawLead.pending, (State) => {
        State.loading = true;
      })
      .addCase(deleteRawLead.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
      })
      .addCase(
        deleteRawLead.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
        }
      );

    // delete polished Lead
    builder
      .addCase(deletePolishedLead.pending, (State) => {
        State.loading = true;
      })
      .addCase(
        deletePolishedLead.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
        }
      )
      .addCase(
        deletePolishedLead.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
        }
      );
  },
});

export const leadReducer = leadSlice.reducer;
